/// <amd-module name="Build/ApprovalsPanel/StageChecksPanel" />
import * as React from "react";

import { Time } from "VSSUI/Components/Time/Time/Time";
import * as DateUtil from "VSSUI/Utilities/Date"
import * as PlatformResources from "VSSUI/Resources.Core";
import { ApprovalOptionsGroup, ApprovalPanel, DeferApprovalArea } from "Build/ApprovalsPanel/ApprovalPanel";
import { IChecksPanelProps, IStageInfo, ReviewMode } from "Build/ApprovalsPanel/ChecksListPanel.props";
import { IChecksPanelService } from "Build/ApprovalsPanel/ChecksPanelService.types";
import { EvaluationOrder, MainApprovalDefTypeId, PostApprovalDefTypeId, PreApprovalDefTypeId } from "Build/ApprovalsPanel/Constants";
import { LogsViewPanel } from "Build/ApprovalsPanel/LogsViewPanel";
import { ManualValidationOptionsGroup, ManualValidationPanel } from "Build/ApprovalsPanel/ManualValidationPanel";
import { getBuildUrl, getResourceUrl } from "Build/ApprovalsPanel/ResourceLinks";
import * as Resources from "Build/ApprovalsPanel/Resources";
import * as Utilities from "Build/ApprovalsPanel/Utilities";
import { ApprovalPermissions, ApprovalStatus, ApprovalUpdateParameters } from "Build/Client/Contracts/Approval";
import {
    ApprovalDetails,
    ApprovalStep,
    CheckDetails,
    CheckListItemType,
    ChecksPanelData,
    ChecksPermission,
    ExclusiveLockDetails,
    ManualValidationDetails,
    TaskCheckDetails
} from "Build/Client/Contracts/DataProvider";
import { ResourcePipelinePermissions } from "Build/Client/Contracts/PipelinePermissions";
import { CheckIssueType, CheckRunStatus } from "Build/Client/Contracts/PipelinesChecks";
import { IConfirmPipelineActionProps } from "Build/Common/Commands/ConfirmPipelineAction/Command.types";
import { CommandComponent } from "Build/Common/Library/Components/CommandComponent";
import { ITfsPageService } from "Tfs/Platform/Page";

import { ITaskCheckLogLineService } from "Build/Common/Library/TaskCheckLogLineService";
import { Button, IButtonProps } from "VSSUI/Button";
import { ButtonGroup } from "VSSUI/ButtonGroup";
import { Icon } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";
import { IListItemDetails, IListRow, List, ListItem } from "VSSUI/List";
import { IMenuItem, MoreButton } from "VSSUI/Menu";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { Panel } from "VSSUI/Panel";
import { Spinner } from "VSSUI/Spinner";
import { Status, StatusSize } from "VSSUI/Status";
import { Tooltip } from "VSSUI/TooltipEx";
import { css } from "VSSUI/Util";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";

import { ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { equals, format, localeFormat } from "VSS/Core/Util/String";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { Components, IVssLayoutManager, useComponentContext } from "VSS/Platform/Layout";

const defaultStageName = "__default";

const StageChecksPanel: React.FunctionComponent<IChecksPanelProps> = (props) => {
    const context = useComponentContext();
    const service = context.pageContext.getService<IChecksPanelService>("IChecksPanelService");
    service.initialize();
    service.fetchData(
        props.runId,
        props.stages.map((s) => s.id),
        props.checkListItemType
    );

    return <StageChecksPanelView {...props} />;
};

const checkRerunNotSupportedList: String[] = [
    Utilities.BranchProtectionPolicyCheckTypeIdString,
    Utilities.BusinessHoursPolicyCheckTypeIdString,
    Utilities.EvaluateArtifactPolicyCheckTypeIdString
]

const checkBypassNotSupportedList: String[] = [
    Utilities.BranchProtectionPolicyCheckTypeIdString,
    Utilities.EvaluateArtifactPolicyCheckTypeIdString
]

export const StageChecksPanelView: React.SFC<IChecksPanelProps> = (props) => {
    const [approvalPanelVisible, setApprovalPanelVisible] = React.useState(false);
    const [manualValidationPanelVisible, setManualValidationPanelVisible] = React.useState(false);
    const [selectedApproval, setSelectedApproval] = React.useState<ApprovalDetails | undefined>(undefined);
    const [selectedManualValidation, setSelectedManualValidation] = React.useState<ManualValidationDetails | undefined>(undefined);
    const [selectedStage, setSelectedStage] = React.useState<string | undefined>(undefined);
    const [logsViewPanelVisible, setLogsViewPanelVisible] = React.useState(false);
    const [selectedTaskCheck, setSelectedTaskCheck] = React.useState<TaskCheckDetails | undefined>(undefined);
    const [showOverlay, setShowOverlay] = React.useState<boolean>(false);
    const [panelDescription, setPanelDescription] = React.useState<string>("");
    const [arePendingChecksPresent, setArePendingChecksPresent] = React.useState<boolean>(false);
    const [projectId, setProjectId] = React.useState<string>("");

    const context = props.componentContext || useComponentContext();
    const service = context.pageContext.getService<IChecksPanelService>("IChecksPanelService");
    const showCheckIssues = isFeatureFlagEnabled(context.pageContext, "Pipelines.Checks.EnableChecksScalabilityPhase1BuildCheckIssues", false);
    const showOrderedChecks = isFeatureFlagEnabled(context.pageContext, "Pipelines.Checks.EnableOrderingChecks", false);

    const onSelectApproval = (approval: ApprovalDetails, stageName: string) => {
        setSelectedApproval(approval);
        setSelectedStage(stageName);
        setApprovalPanelVisible(true);
    };

    const onSelectManualValidation = (manualValidation: ManualValidationDetails, stageName: string) => {
        setSelectedManualValidation(manualValidation);
        setSelectedStage(stageName);
        setManualValidationPanelVisible(true);
    };

    const onSelectTaskCheck = (taskCheck: TaskCheckDetails, arePendingChecksPresentInStage: boolean) => {
        setLogsViewPanelVisible(true);
        setSelectedTaskCheck(taskCheck);
        setArePendingChecksPresent(arePendingChecksPresentInStage);
    };

    const onApprovalPanelBackClick = (event: React.SyntheticEvent<HTMLElement>) => {
        setSelectedApproval(undefined);
        setApprovalPanelVisible(false);
    };

    const onManualValidationPanelBackClick = (event: React.SyntheticEvent<HTMLElement>) => {
        setSelectedManualValidation(undefined);
        setManualValidationPanelVisible(false);
    };

    const onLogsViewPanelBackClick = (event: React.SyntheticEvent<HTMLElement>) => {
        setSelectedTaskCheck(undefined);
        setLogsViewPanelVisible(false);
    };

    let panelTitle = Resources.DefaultPanelTitle;
    if (props.reviewMode === ReviewMode.AllPending) {
        panelTitle = Resources.AllPendingPanelTitle;
    } else if (props.reviewMode === ReviewMode.Stage && props.stages && props.stages[0] && props.stages[0].name !== defaultStageName) {
        switch (props.checkListItemType) {
            case CheckListItemType.All:
                panelTitle = format(Resources.StagePanelTitleCommon, props.stages[0].name);
                break;
            case CheckListItemType.Checks:
                panelTitle = format(Resources.StagePanelTitleChecks, props.stages[0].name);
                break;
            case CheckListItemType.ManualValidation:
                panelTitle = format(Resources.StagePanelTitleManualValidation, props.stages[0].name);
                break;
        }
    }

    const isDialogOpen = new ObservableValue<boolean>(false);
    const dialogData = new ObservableValue<ICommentDialogData>({ status: ApprovalStatus.Approved, approvals: [] });

    const dismissDialog = () => {
        isDialogOpen.value = false;
    };

    const openDialog = (status: ApprovalStatus, approvals: ApprovalDetails[] | ManualValidationDetails[]) => {
        isDialogOpen.value = true;
        dialogData.value = {
            status: status,
            approvals: approvals
        };
    };

    const submitApprovals = (status: ApprovalStatus, approvals: ApprovalDetails[] | ManualValidationDetails[], message?: string, deferredTo?: Date | null) => {
        const updates = (approvals as Array<ApprovalDetails | ManualValidationDetails>).map((a) => {
            return {
                approvalId: a.id,
                status: status,
                comment: message || "",
                deferredTo: deferredTo
            } as ApprovalUpdateParameters;
        });
        setShowOverlay(true);
        props.onSubmitBulkApproval(updates);
    };

    const rerunCheck = (check: any) => {
        props.onRerunCheck(check.resource.checkId, check.taskCheckData.planId);
    };

    const bypassCheck = (check: CheckDetails) => {
        props.onBypassCheck(check.id, check.checkSuiteId);
    };

    const reassignStep = (approval: ApprovalDetails, step: ApprovalStep, reassignTo: IdentityRef, message: string): Promise<void> => {
        let updates: ApprovalUpdateParameters[] = new Array<ApprovalUpdateParameters>();

        let reassignBody = {
            approvalId: approval.id,
            assignedApprover: step.assignedApprover,
            reassignTo: reassignTo,
            comment: message || ""
        } as ApprovalUpdateParameters;
        updates.push(reassignBody);

        return props.onReassignApprovals(updates);
    };

    const showStage = (props.reviewMode === ReviewMode.Stage && !!props.onStageBackClick);

    return (
        <Panel
            backButtonProps={showStage? ({ onClick: props.onStageBackClick }) as IButtonProps: undefined }
            onDismiss={props.onDismiss}
            contentClassName="pipelines-checks-panel"
            titleProps={{ text: panelTitle }}
            description={panelDescription}
            overlayContent={showOverlay ? <Spinner /> : undefined}
        >
            <Observer stages={service.data}>
                {(observerProps: { stages: ChecksPanelData[] }) => {
                    const stagesWithChecks: ChecksPanelData[] = [];
                    let hasNonApprovalChecks = false;

                    // Approvals
                    const myApprovalStages = observerProps.stages.filter((stage) => {
                        return stage.approvals && stage.approvals.some((a) => Utilities.isCheckPermissionUpdate(a.permissions));
                    });
                    const otherApprovalStages = observerProps.stages.filter((stage) => {
                        return stage.approvals && stage.approvals.length > 0 && stage.approvals.every((a) => a.permissions === ChecksPermission.View);
                    });
                    const sortedApprovalStages = myApprovalStages.concat(otherApprovalStages);
                    sortedApprovalStages.forEach((x) => stagesWithChecks.push(x));

                    // ManualValidations
                    const myManualValidationStages = observerProps.stages.filter((stage) => {
                        return stage.manualValidations && stage.manualValidations.some((a) => Utilities.isCheckPermissionUpdate(a.permissions));
                    });
                    const otherManualValidationStages = observerProps.stages.filter((stage) => {
                        return (
                            stage.manualValidations &&
                            stage.manualValidations.length > 0 &&
                            stage.manualValidations.every((a) => a.permissions === ChecksPermission.View)
                        );
                    });
                    const sortedManualValidationStages = myManualValidationStages.concat(otherManualValidationStages);

                    //Check if stage is already added due to approvals
                    for (let stage of sortedManualValidationStages) {
                        const hasApprovals = stage.approvals && stage.approvals.length > 0;
                        if (!hasApprovals) {
                            stagesWithChecks.push(stage);
                        }
                    }

                    // Add other types of checks
                    for (let stage of observerProps.stages) {
                        const hasApprovals = stage.approvals && stage.approvals.length > 0;
                        const hasManualValidations = stage.manualValidations && stage.manualValidations.length > 0;
                        const hasTaskChecks = stage.taskChecks && stage.taskChecks.length > 0;
                        const hasExclusiveLockChecks = stage.exclusiveLockChecks && stage.exclusiveLockChecks.length > 0;
                        const hasNonAuthOtherChecks =
                            stage.checks && stage.checks.filter((x) => !equals(x.typeId, Utilities.AuthorizationCheckTypeIdString, true)).length > 0;
                        if (hasTaskChecks || hasExclusiveLockChecks || hasNonAuthOtherChecks) {
                            if (!hasApprovals && !hasManualValidations) {
                                stagesWithChecks.push(stage);
                            }
                            hasNonApprovalChecks = true;
                        }
                    }

                    setProjectId(observerProps.stages && observerProps.stages.length > 0 ? observerProps.stages[0].projectId : "");

                    // permissions
                    const stagesWithPermissionChecks = observerProps.stages.filter(
                        (stage) =>
                            stage.checks &&
                            stage.checks.length > 0 &&
                            stage.checks.every((c) => equals(c.typeId, Utilities.AuthorizationCheckTypeIdString, true))
                    );
                    const myPermissionStages = stagesWithPermissionChecks.filter((stage) =>
                        stage.checks.some((c) => Utilities.isCheckPermissionUpdate(c.permissions))
                    );
                    const otherPermissionStages = stagesWithPermissionChecks.filter(
                        (stage) => stage.checks.length > 0 && stage.checks.every((c) => c.permissions === ChecksPermission.View)
                    );
                    const sortedPermissionStages = myPermissionStages.concat(otherPermissionStages);

                    const bypassChecksPanel = shouldBypassChecksPanel(stagesWithChecks, sortedPermissionStages, hasNonApprovalChecks);

                    if (props.reviewMode === ReviewMode.Stage && stagesWithChecks.length === 1) {
                        let { stageTimeoutMessage, stageTimeoutMessageToolTip } = getStageTimeoutDetails(stagesWithChecks[0]);
                        setPanelDescription(stageTimeoutMessage!);
                    } else {
                        setPanelDescription("");
                    }

                    return (
                        <div className="flex-column rhythm-vertical-32 pipelines-checks-container">
                            {!bypassChecksPanel &&
                                stagesWithChecks.map((stageCheckData) => {

                                    const onViewStageChecksClicked = () => {
                                        if (!!props.onViewStageClick) {
                                            props.onViewStageClick({
                                                id: stageCheckData.stageId,
                                                name: stageCheckData.stageName} as IStageInfo);
                                        }
                                    }



                                    return (showOrderedChecks
                                        ?
                                        <ChecksStageGroupOrdered
                                            stageData={stageCheckData}
                                            key={"StageGroup" + stageCheckData.stageId}
                                            showCheckIssues={showCheckIssues}
                                            onSelectTaskCheck={onSelectTaskCheck}
                                            onSelectApproval={onSelectApproval}
                                            onSelectManualValidation={onSelectManualValidation}
                                            onViewStageChecksClicked={!!props.onViewStageClick ? onViewStageChecksClicked : undefined}
                                            reviewMode={props.reviewMode}
                                            submitApprovals={submitApprovals}
                                            openReviewDialog={openDialog}
                                            onRerunCheck={rerunCheck}
                                            onBypassCheck={bypassCheck}
                                            isChecksOrderingEnabled={showOrderedChecks}
                                        />
                                        :
                                        <ChecksStageGroup
                                            stageData={stageCheckData}
                                            key={"StageGroup" + stageCheckData.stageId}
                                            showCheckIssues={showCheckIssues}
                                            onSelectTaskCheck={onSelectTaskCheck}
                                            onSelectApproval={onSelectApproval}
                                            onSelectManualValidation={onSelectManualValidation}
                                            onViewStageChecksClicked={!!props.onViewStageClick ? onViewStageChecksClicked : undefined}
                                            reviewMode={props.reviewMode}
                                            submitApprovals={submitApprovals}
                                            openReviewDialog={openDialog}
                                            onRerunCheck={rerunCheck}
                                            onBypassCheck={bypassCheck}
                                            isChecksOrderingEnabled={showOrderedChecks}
                                        />
                                    );
                                })}
                            {sortedPermissionStages.map((checksDataForStage) => {
                                return (
                                    checksDataForStage.checks.length > 0 && (
                                        <PermissionStageGroup
                                            key={"StageGroup" + checksDataForStage.stageId}
                                            pipelineId={props.pipelineId}
                                            stageData={checksDataForStage}
                                            onPermitResource={props.onPermitResource}
                                            reviewMode={props.reviewMode}
                                        />
                                    )
                                );
                            })}
                            {bypassChecksPanel && stagesWithChecks[0].approvals && (
                                <ApprovalPanel
                                    approval={stagesWithChecks[0].approvals[0]}
                                    stageName={stagesWithChecks[0].stageName}
                                    onBackClick={props.onDismiss}
                                    onDismiss={props.onDismiss}
                                    onSubmitApprovals={submitApprovals}
                                    onReassignStep={reassignStep}
                                />
                            )}
                            {bypassChecksPanel && stagesWithChecks[0].manualValidations && (
                                <ManualValidationPanel
                                    manualValidation={stagesWithChecks[0].manualValidations[0]}
                                    stageName={stagesWithChecks[0].stageName}
                                    onBackClick={props.onDismiss}
                                    onDismiss={props.onDismiss}
                                    onSubmitManualValidations={submitApprovals}
                                />
                            )}

                            {bypassChecksPanel && stagesWithChecks[0].taskChecks && (
                                <LogsViewPanel
                                    check={stagesWithChecks[0].taskChecks[0]}
                                    arePendingChecksPresent={stagesWithChecks[0].taskChecks[0].status === CheckRunStatus.Running}
                                    projectId={projectId}
                                    onBackClick={props.onDismiss}
                                    onDismiss={props.onDismiss}
                                />
                            )}
                        </div>
                    );
                }}
            </Observer>
            {approvalPanelVisible && (
                <ApprovalPanel
                    approval={selectedApproval!}
                    stageName={selectedStage!}
                    onBackClick={onApprovalPanelBackClick}
                    onDismiss={props.onDismiss}
                    onSubmitApprovals={submitApprovals}
                    onReassignStep={reassignStep}
                />
            )}
            {manualValidationPanelVisible && (
                <ManualValidationPanel
                    manualValidation={selectedManualValidation!}
                    stageName={selectedStage!}
                    onBackClick={onManualValidationPanelBackClick}
                    onDismiss={props.onDismiss}
                    onSubmitManualValidations={submitApprovals}
                />
            )}
            {logsViewPanelVisible && (
                <LogsViewPanel
                    check={selectedTaskCheck!}
                    arePendingChecksPresent={arePendingChecksPresent}
                    projectId={projectId}
                    onBackClick={onLogsViewPanelBackClick}
                    onDismiss={props.onDismiss}
                />
            )}
        </Panel>
    );
};

interface IPermissionStageGroup {
    pipelineId: number;
    stageData: ChecksPanelData;
    onPermitResource: (permissionsRequest: ResourcePipelinePermissions, resourceType: string, resourceId: string) => void;
    reviewMode: ReviewMode;
}

const PermissionStageGroup: React.SFC<IPermissionStageGroup> = (props) => {
    const permissions = new ArrayItemProvider<CheckDetails>(props.stageData.checks);
    const stageName = props.stageData.stageName;
    const context = useComponentContext();
    const shouldShowStage = props.reviewMode === ReviewMode.AllPending && stageName !== defaultStageName;

    const permitResource = (check: CheckDetails) => {
        const permissions = {
            pipelines: [
                {
                    id: props.pipelineId,
                    authorized: true
                }
            ]
        } as ResourcePipelinePermissions;

        const resourceType = check.resource.type;

        const onConfirm = () => {
            props.onPermitResource(permissions, resourceType, check.resource.id);
        };

        const layoutManager = context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout((onDismiss) => {
            const props: IConfirmPipelineActionProps = {
                title: Resources.PermitConfirmDialogTitle,
                message: localeFormat(Resources.PermitConfirmDialogDescription, Utilities.getResourceTypeString(resourceType), check.resource.name),
                onClose: onDismiss,
                cancelLabel: Resources.Cancel,
                onConfirm: onConfirm,
                confirmLabel: Resources.Permit
            };

            return React.createElement(
                CommandComponent,
                {
                    commandType: "ciDConfirmPipelineComponent",
                    ...props
                },
                null
            );
        });
    };

    return (
        <div>
            {shouldShowStage && (
                <div className="flex-row body-m">
                    <div className="flex-column flex-grow justify-end">
                        <div className="flex-row font-weight-semibold checks-group-header-title">
                        <span className="title-s text-ellipsis stage-header">{props.stageData.stageName}</span>
                        </div>
                    </div>
                </div>
            )}

            <List
                className="pipelines-checks-container"
                itemProvider={permissions}
                renderRow={(index: number, check: CheckDetails, details: IListItemDetails<CheckDetails>) => {
                    const resourceName = check.resource.name;
                    const permitButtonId = format("pm-{0}-{1}-{2}", props.stageData.stageId, check.resource.type, check.resource.id);
                    const resourceIconName = check.resource ? Utilities.getResourceTypeIcons(check.resource.type) : "Server";
                    const resourceType = check.resource ? Utilities.getResourceTypeString(check.resource.type) : "";

                    return (
                        <ListItem key={"ci-" + index} index={index} details={details}>
                            <div className="pipelines-permission-group flex-row flex-grow flex-center h-scroll-hidden">
                                <Icon className="pipelines-resource-icon" iconName="Lock" />
                                <div className="pipelines-permission-row flex-column flex-grow h-scroll-hidden">
                                    <div className="flex-row flex-center h-scroll-hidden">
                                        <span className="checks-row-title body-m text-ellipsis">{Resources.Permission}</span>
                                        <Icon className="pipelines-resource-icon secondary-text" iconName={resourceIconName} />
                                        <span className="pipelines-resource body-s text-ellipsis secondary-text">{resourceType}</span>
                                        <span className="pipelines-resource body-s text-ellipsis">
                                            <Link className="pipelines-resource-link" href={getResourceUrl(context.pageContext, check.resource)}>
                                                {resourceName}
                                            </Link>
                                        </span>
                                    </div>
                                    <span className="body-s text-ellipsis secondary-text">{Resources.PermissionSecondary}</span>
                                </div>
                                {Utilities.isCheckPermissionUpdate(check.permissions) && (
                                    <ButtonGroup className="flex-grow">
                                        <Button id={permitButtonId} primary={true} text={Resources.Permit} onClick={() => permitResource(check)} />
                                    </ButtonGroup>
                                )}
                            </div>
                        </ListItem>
                    );
                }}
            />
        </div>
    );
};

interface ICommentDialogData {
    status: ApprovalStatus;
    approvals: ApprovalDetails[] | ManualValidationDetails[];
}

interface ICheckStageGroupProps {
    reviewMode: ReviewMode;
    stageData: ChecksPanelData;
    showCheckIssues?: boolean;
    isChecksOrderingEnabled: boolean;
    onSelectApproval: (approval: ApprovalDetails, name: string) => void;
    onSelectManualValidation: (manualValidation: ManualValidationDetails, name: string) => void;
    onSelectTaskCheck: (taskCheck: TaskCheckDetails, arePendingChecksPresentInStage: boolean) => void;
    onViewStageChecksClicked?: () => void;
    submitApprovals: (status: ApprovalStatus, approvals: ApprovalDetails[] | ManualValidationDetails[], message?: string, deferredTo?: Date | null) => void;
    openReviewDialog: (status: ApprovalStatus, approvals: ApprovalDetails[] | ManualValidationDetails[]) => void;
    onRerunCheck: (check: any) => void;
    onBypassCheck: (check: any) => void;
}

const ChecksStageGroup: React.SFC<ICheckStageGroupProps> = (props) => {
    let isUserBlockedFromApproving = props.stageData.approvals && isCurrentUserBlockedInAnyApproval(props.stageData.approvals);

    //To eliminate those approvals which are in sequence and approver is waiting on sequence.
    const isApprovalAllowed = (approval: ApprovalDetails): boolean => {
        let allowedApprovals = approval.steps.filter((a) => a.status === ApprovalStatus.Pending && a.permissions === ApprovalPermissions.Update);
        return allowedApprovals.length > 0;
    };
    const myApprovals = props.stageData.approvals
        ? new ObservableArray<ApprovalDetails>(
              props.stageData.approvals.filter((a) => Utilities.isCheckPermissionUpdate(a.permissions) && isApprovalAllowed(a))
          )
        : new ObservableArray([]);
    const otherApprovals = props.stageData.approvals
        ? new ObservableArray<ApprovalDetails>(getOtherApprovals(props.stageData.approvals).filter((a) => !myApprovals.value.includes(a)))
        : new ObservableArray([]);
    const myManualValidations = props.stageData.manualValidations
        ? new ObservableArray<ManualValidationDetails>(props.stageData.manualValidations.filter((a) => Utilities.isCheckPermissionUpdate(a.permissions)))
        : new ObservableArray([]);
    const otherManualValidations = props.stageData.manualValidations
        ? new ObservableArray<ManualValidationDetails>(getOtherManualValidations(props.stageData.manualValidations))
        : new ObservableArray([]);
    const taskChecks = props.stageData.taskChecks ? new ObservableArray<TaskCheckDetails>(props.stageData.taskChecks) : new ObservableArray([]);
    const extendsChecksItems = props.stageData.checks
        ? props.stageData.checks.filter((c) => equals(c.typeId, Utilities.ExtendsCheckTypeIdString, true))
        : null;
    const extendsChecks = extendsChecksItems ? new ObservableArray<CheckDetails>(extendsChecksItems) : new ObservableArray([]);
    const exclusiveLockChecks = props.stageData.exclusiveLockChecks
        ? new ObservableArray<ExclusiveLockDetails>(props.stageData.exclusiveLockChecks)
        : new ObservableArray([]);

    const arePendingChecksPresentInStage = arePendingChecksPresent(props.stageData);

    const { stageTimeoutMessage, stageTimeoutMessageToolTip } = getStageTimeoutDetails(props.stageData);

    const onSubmitReview = (status: ApprovalStatus, commentMessage: string, deferredTo: Date | null = null, isManualValidation: boolean = false) => {
        if (isManualValidation) {
            props.submitApprovals(status, myManualValidations.value, commentMessage, deferredTo);
        } else {
            props.submitApprovals(status, myApprovals.value, commentMessage, deferredTo)
        }
    };

    const openReviewDialog = (status: ApprovalStatus) => {
        props.openReviewDialog(status, myApprovals.value);
    };

    return (
        <div>
            <StageGroupHeader
                myApprovalsCount={myApprovals.length}
                myManualValidationsCount={myManualValidations.length}
                otherChecksCount={otherApprovals.length + otherManualValidations.length}
                stageName={props.stageData.stageName}
                stageTimeoutString={stageTimeoutMessage!}
                stageTimeoutStringToolTip={stageTimeoutMessageToolTip!}
                reviewMode={props.reviewMode}
                openReviewDialog={openReviewDialog}
                onViewStageChecksClicked={props.onViewStageChecksClicked}
            />

            {myApprovals.length > 0 && (
                <div>
                    <ApprovalGroup
                        itemProvider={myApprovals}
                        isManualValidation={false}
                        showOptions={true}
                        arePendingChecksPresent={arePendingChecksPresentInStage}
                        onSelectApproval={props.onSelectApproval}
                        stageName={props.stageData.stageName}
                        onSubmit={onSubmitReview}
                        isChecksOrderingEnabled={props.isChecksOrderingEnabled}
                        onBypassCheck={props.onBypassCheck}
                    />
                </div>
            )}
            {isUserBlockedFromApproving && (
                <MessageCard className="requester-blocked-message-card" severity={MessageCardSeverity.Warning}>
                    {Resources.CurrentUserCannotApproveMessage}
                </MessageCard>
            )}
            {otherApprovals.length > 0 && (
                <ApprovalGroup
                    itemProvider={otherApprovals}
                    isManualValidation={false}
                    showOptions={false}
                    arePendingChecksPresent={arePendingChecksPresentInStage}
                    onSelectApproval={props.onSelectApproval}
                    stageName={props.stageData.stageName}
                    onSubmit={onSubmitReview}
                    isChecksOrderingEnabled={props.isChecksOrderingEnabled}
                    onBypassCheck={props.onBypassCheck}
                />
            )}
            {taskChecks.length > 0 && (
                <ChecksGroup
                    checks={taskChecks}
                    onSelectCheck={props.onSelectTaskCheck}
                    arePendingChecksPresent={arePendingChecksPresentInStage}
                    showCheckIssues={props.showCheckIssues}
                    onRerunCheck={props.onRerunCheck}
                    onBypassCheck={props.onBypassCheck}
                />
            )}
            {extendsChecks.length > 0 && (
                <ExtendsGroup
                    checks={extendsChecks}
                />
            )}
            {exclusiveLockChecks.length > 0 && (
                <ExclusiveLockGroup
                    checks={exclusiveLockChecks}
                />
            )}
            {myManualValidations.length > 0 && (
                <div>
                    <ApprovalGroup
                        itemProvider={myManualValidations}
                        isManualValidation={true}
                        showOptions={true}
                        onSelectApproval={props.onSelectManualValidation}
                        stageName={props.stageData.stageName}
                        onSubmit={onSubmitReview}
                        isChecksOrderingEnabled={props.isChecksOrderingEnabled}
                        onBypassCheck={props.onBypassCheck}
                    />
                </div>
            )}
            {otherManualValidations.length > 0 && (
                <ApprovalGroup
                    itemProvider={otherManualValidations}
                    isManualValidation={true}
                    showOptions={false}
                    onSelectApproval={props.onSelectManualValidation}
                    stageName={props.stageData.stageName}
                    onSubmit={onSubmitReview}
                    isChecksOrderingEnabled={props.isChecksOrderingEnabled}
                    onBypassCheck={props.onBypassCheck}
                />
            )}
        </div>
    );
};

const ChecksStageGroupOrdered: React.SFC<ICheckStageGroupProps> = (props) => {
    // Groups checks by evaluation order
    const groupByEvaluationOrder = <T extends CheckDetails>(checks:  T[]) => {
        return checks.reduce((result, check) =>  {
            var evaluationOrderList = result.get(check.evaluationOrder) || [];
            evaluationOrderList.push(check);
            result.set(check.evaluationOrder, evaluationOrderList);
            return result;
        }, new Map<EvaluationOrder, T[]>());
    }

    //To eliminate those approvals which are in sequence and approver is waiting on sequence.
    const isApprovalAllowed = (approval: ApprovalDetails): boolean => {
        let allowedApprovals = approval.steps.filter((a) => (a.status === ApprovalStatus.Pending || a.status === ApprovalStatus.Deferred) && a.permissions === ApprovalPermissions.Update);
        return allowedApprovals.length > 0;
    };

    const openReviewDialog = (status: ApprovalStatus, myApprovals: ApprovalDetails[]) => {
        props.openReviewDialog(status, myApprovals);
    };

    // Lists of all check types in stage.
    const myApprovals = props.stageData.approvals ? props.stageData.approvals.filter((a) => Utilities.isCheckPermissionUpdate(a.permissions) && isApprovalAllowed(a)) : [];
    const otherApprovals = props.stageData.approvals ? getOtherApprovals(props.stageData.approvals).filter((a) => !myApprovals.includes(a)) : [];
    const myManualValidations = props.stageData.manualValidations ? props.stageData.manualValidations.filter((a) => Utilities.isCheckPermissionUpdate( a.permissions)): [];
    const otherManualValidations = props.stageData.manualValidations ? getOtherManualValidations(props.stageData.manualValidations): [];
    const taskChecks = props.stageData.taskChecks || []
    const extendsChecks = props.stageData.checks ? props.stageData.checks.filter((c) => equals(c.typeId, Utilities.ExtendsCheckTypeIdString, true)) : [];
    const exclusiveLockChecks = props.stageData.exclusiveLockChecks || [];

    // Groups of all check types in stage grouped by evaluationOrder
    const myApprovalsGrouped = groupByEvaluationOrder(myApprovals);
    const otherApprovalsGrouped = groupByEvaluationOrder(otherApprovals);
    const myManualValidationsGrouped = groupByEvaluationOrder(myManualValidations);
    const otherManualValidationsGrouped = groupByEvaluationOrder(otherManualValidations);
    const taskChecksGrouped = groupByEvaluationOrder(taskChecks);
    const extendsChecksGrouped = groupByEvaluationOrder(extendsChecks);
    const exclusiveLockChecksGrouped = groupByEvaluationOrder(exclusiveLockChecks);

    const { stageTimeoutMessage, stageTimeoutMessageToolTip } = getStageTimeoutDetails(props.stageData);


    const evaluationOrderPhases = Object.values(EvaluationOrder)
        .filter(key => Number.isInteger(Number(key)));

    let isFirstEvaluationPhase = true
    const evaluationPhases = evaluationOrderPhases.map((key) => {
            const phase = key as number;
            var myApprovalsPhase = myApprovalsGrouped.get(phase) || [];
            var myManualValidationsPhase = myManualValidationsGrouped.get(phase) || [];
            var otherApprovalsPhase = otherApprovalsGrouped.get(phase) || [];
            var otherManualValidationsPhase = otherManualValidationsGrouped.get(phase) || [];
            var taskChecksPhase = taskChecksGrouped.get(phase) || [];
            var extendsChecksPhase = extendsChecksGrouped.get(phase) || [];
            var exclusiveLockChecksPhase = exclusiveLockChecksGrouped.get(phase) || [];

            if(myApprovalsPhase.length <= 0
                && myManualValidationsPhase.length <= 0
                && otherApprovalsPhase.length <= 0
                && otherManualValidationsPhase.length <= 0
                && taskChecksPhase.length <= 0
                && extendsChecksPhase.length <= 0
                && exclusiveLockChecksPhase.length <= 0) {
                return;
            }

            let renderSeperator = true
            if(isFirstEvaluationPhase) {
                renderSeperator = false;
                isFirstEvaluationPhase = false;
            }

            return <ChecksStageOrdered
                    {...props}
                    evaluationOrder={phase}
                    myApprovals={new ObservableArray(myApprovalsPhase)}
                    myManualValidations={new ObservableArray(myManualValidationsPhase)}
                    otherApprovals={new ObservableArray(otherApprovalsPhase)}
                    otherManualValidations={new ObservableArray(otherManualValidationsPhase)}
                    taskChecks={new ObservableArray(taskChecksPhase)}
                    extendsChecks={new ObservableArray(extendsChecksPhase)}
                    exclusiveLockChecks={new ObservableArray(exclusiveLockChecksPhase)}
                    renderSeperator={renderSeperator}
                    key={"phase_" + phase}
                />;
        });

    return (
        <div>
            <StageGroupHeader
                myApprovalsCount={myApprovals.length}
                myManualValidationsCount={myManualValidations.length}
                otherChecksCount={otherApprovals.length + otherManualValidations.length}
                stageName={props.stageData.stageName}
                stageTimeoutString={stageTimeoutMessage!}
                stageTimeoutStringToolTip={stageTimeoutMessageToolTip!}
                reviewMode={props.reviewMode}
                openReviewDialog={(status: ApprovalStatus) => openReviewDialog(status, myApprovals)}
                onViewStageChecksClicked={props.onViewStageChecksClicked}
            />
            {evaluationPhases}
        </div>
    );
}

interface IChecksStageOrderedProps {
    stageData: ChecksPanelData;
    showCheckIssues?: boolean;
    evaluationOrder: number;
    onSelectApproval: (approval: ApprovalDetails, name: string) => void;
    onSelectManualValidation: (manualValidation: ManualValidationDetails, name: string) => void;
    onSelectTaskCheck: (taskCheck: TaskCheckDetails, arePendingChecksPresentInStage: boolean) => void;
    submitApprovals: (status: ApprovalStatus, approvals: ApprovalDetails[] | ManualValidationDetails[], message?: string, deferredTo?: Date | null) => void;
    onRerunCheck: (check: any) => void;
    onBypassCheck: (check: any) => void;
    renderSeperator: boolean;
    isChecksOrderingEnabled: boolean;
    myApprovals: ObservableArray<ApprovalDetails>;
    myManualValidations: ObservableArray<ManualValidationDetails>;
    otherApprovals: ObservableArray<ApprovalDetails>;
    otherManualValidations: ObservableArray<ManualValidationDetails>;
    taskChecks: ObservableArray<TaskCheckDetails>;
    extendsChecks: ObservableArray<CheckDetails>;
    exclusiveLockChecks: ObservableArray<ExclusiveLockDetails>;
}

const ChecksStageOrdered: React.SFC<IChecksStageOrderedProps> = (props) => {
    const isUserBlockedFromApproving = props.stageData.approvals && isCurrentUserBlockedInAnyApproval(props.stageData.approvals);
    const arePendingChecksPresentInStage = arePendingChecksPresent(props.stageData);

    const onSubmitReview = (status: ApprovalStatus, commentMessage: string, deferredTo: Date | null, isManualValidation: boolean = false) => {
        if (isManualValidation) {
            props.submitApprovals(status, props.myManualValidations.value, commentMessage, deferredTo);
        } else {
            props.submitApprovals(status, props.myApprovals.value, commentMessage, deferredTo);
        }
    };

    return (
        <div className={css(props.renderSeperator && "stage-checks-ordered")}>
            {props.myApprovals.length > 0 && (
                <div>
                    <ApprovalGroup
                        itemProvider={props.myApprovals}
                        isManualValidation={false}
                        showOptions={true}
                        arePendingChecksPresent={arePendingChecksPresentInStage}
                        onSelectApproval={props.onSelectApproval}
                        stageName={props.stageData.stageName}
                        onSubmit={onSubmitReview}
                        isChecksOrderingEnabled={props.isChecksOrderingEnabled}
                        onBypassCheck={props.onBypassCheck}
                    />
                </div>
            )}
            {isUserBlockedFromApproving && (
                <MessageCard className="requester-blocked-message-card" severity={MessageCardSeverity.Warning}>
                    {Resources.CurrentUserCannotApproveMessage}
                </MessageCard>
            )}
            {props.otherApprovals.length > 0 && (
                <ApprovalGroup
                    itemProvider={props.otherApprovals}
                    isManualValidation={false}
                    showOptions={false}
                    arePendingChecksPresent={arePendingChecksPresentInStage}
                    onSelectApproval={props.onSelectApproval}
                    stageName={props.stageData.stageName}
                    onSubmit={onSubmitReview}
                    isChecksOrderingEnabled={props.isChecksOrderingEnabled}
                    onBypassCheck={props.onBypassCheck}
                />
            )}
            {props.taskChecks.length > 0 && (
                <ChecksGroup
                    checks={props.taskChecks}
                    onSelectCheck={props.onSelectTaskCheck}
                    arePendingChecksPresent={arePendingChecksPresentInStage}
                    showCheckIssues={props.showCheckIssues}
                    onRerunCheck={props.onRerunCheck}
                    onBypassCheck={props.onBypassCheck}
                />
            )}
            {props.extendsChecks.length > 0 && (
                <ExtendsGroup
                    checks={props.extendsChecks}
                />
            )}
            {props.exclusiveLockChecks.length > 0 && (
                <ExclusiveLockGroup
                    checks={props.exclusiveLockChecks}
                />
            )}
            {props.myManualValidations.length > 0 && (
                <div>
                    <ApprovalGroup
                        itemProvider={props.myManualValidations}
                        isManualValidation={true}
                        showOptions={true}
                        onSelectApproval={props.onSelectManualValidation}
                        stageName={props.stageData.stageName}
                        onSubmit={onSubmitReview}
                        isChecksOrderingEnabled={props.isChecksOrderingEnabled}
                        onBypassCheck={props.onBypassCheck}
                    />
                </div>
            )}
            {props.otherManualValidations.length > 0 && (
                <ApprovalGroup
                    itemProvider={props.otherManualValidations}
                    isManualValidation={true}
                    showOptions={false}
                    onSelectApproval={props.onSelectManualValidation}
                    stageName={props.stageData.stageName}
                    onSubmit={onSubmitReview}
                    isChecksOrderingEnabled={props.isChecksOrderingEnabled}
                    onBypassCheck={props.onBypassCheck}
                />
            )}
        </div>
    );
};

interface IApprovalGroupProps {
    itemProvider: ObservableArray<ApprovalDetails> | ObservableArray<ManualValidationDetails>;
    stageName: string;
    isManualValidation: boolean;
    showOptions: boolean;
    isChecksOrderingEnabled: boolean;
    arePendingChecksPresent?: boolean;
    onSelectApproval: (approval: ApprovalDetails | ManualValidationDetails, stageName: string) => void;
    onSubmit: (status: ApprovalStatus, message: string, deferredTo: Date | null) => void;
    onBypassCheck: (check: any) => void;
}

const ApprovalGroup: React.SFC<IApprovalGroupProps> = (props) => {
    const approvalClickable =(item: ApprovalDetails): boolean => {
        return !props.isChecksOrderingEnabled ||
        (item.status !== CheckRunStatus.Canceled
            && item.status !== CheckRunStatus.TimedOut
            && item.status !== CheckRunStatus.Queued
            && item.status !== CheckRunStatus.None
        )
    }

    const isDeferredApprovalsEnabled = isFeatureFlagEnabled(useComponentContext().pageContext, "Pipelines.Approvals.EnableDeferredApprovals", false);

    const onListItemActivate = (event: React.SyntheticEvent<HTMLElement>, listRow: IListRow<ApprovalDetails> | IListRow<ManualValidationDetails>) => {
        if (!event.defaultPrevented && approvalClickable(listRow.data)) {
            props.onSelectApproval(listRow.data, props.stageName);
        }
    };

    const renderApproval = (
        rowIndex: number,
        item: ApprovalDetails | ManualValidationDetails,
        details: IListItemDetails<ApprovalDetails> | IListItemDetails<ManualValidationDetails>
    ) => {
        const approvalStatus = Utilities.checksToApprovalStatus(item.status);
        const checkName = getCheckName(item);
        const statusIndicatorData = Utilities.getApprovalStatusIndicatorData(approvalStatus);

        if (statusIndicatorData.label === Resources.WaitingApproval && item.instructions) {
            statusIndicatorData.label = format(Resources.WaitingApprovalFormat, "\"" + item.instructions + "\"")
        }
        else if (approvalStatus === ApprovalStatus.Skipped) {
            let modifiedByIdentity = Utilities.getCheckRunUpdateModifiedByIdentity(CheckRunStatus.Bypassed, item.resultUpdates);
            if (modifiedByIdentity) {
                statusIndicatorData.label = format(Resources.BypassedBy, modifiedByIdentity.displayName);
            }
        }

        // Do a shallow copy as list props is read only object
        if(props.isChecksOrderingEnabled) {
            details.listProps = {
                ...details.listProps,
                singleClickActivation: approvalClickable(item)
            };
        }

        return (
            <>
                <CheckListItem
                    rowIndex={rowIndex}
                    check={item}
                    details={details}
                    checkName={checkName}
                    statusIndicator={statusIndicatorData}
                    onBypassCheck={props.onBypassCheck}
                    arePendingChecksPresent={props.arePendingChecksPresent}
                />
                {item.status !== CheckRunStatus.Deferred && props.showOptions && rowIndex === (props.itemProvider.length-1) && !props.isManualValidation &&
                    <ApprovalOptionsGroup isApprovalInSequence={false} myApprovalsCount={props.itemProvider.length} useUpdatedChecksFormat={true} onSubmit={props.onSubmit} />
                }
                {props.showOptions && rowIndex === (props.itemProvider.length-1) && props.isManualValidation &&
                    <ManualValidationOptionsGroup myManualValidationsCount={props.itemProvider.length} useUpdatedChecksFormat={true} onSubmit={props.onSubmit} />
                }
                {isDeferredApprovalsEnabled && item.status === CheckRunStatus.Deferred &&
                    <DeferedApprovalGroup deferredTo={item.steps[0].deferredTo} comment={item.steps[0].comment} onSubmit={props.onSubmit} />
                }
            </>
        );
    };

    return (
        <div className={props.showOptions ? "approval-group" : ""}>
            <List
                itemProvider={props.itemProvider}
                renderRow={renderApproval}
                singleClickActivation={true}
                width="100%"
                onActivate={onListItemActivate}
            />
        </div>
    );
};

interface IDeferedApprovalGroupProps {
    deferredTo: Date;
    comment: string;
    onSubmit: (status: ApprovalStatus, message: string, deferredTo: Date | null) => void;
}

const DeferedApprovalGroup: React.SFC<IDeferedApprovalGroupProps> = (props) => {
    const onDefer = (date: Date) => {
        props.onSubmit(ApprovalStatus.Deferred, props.comment, date);
    }

    const [showEditDeferredTo, setShowEditDeferredTo] = React.useState<boolean>(false);

    return (
        <div>
            <div className={css("flex-column", "approval-options")}>
                <div className="defer-deployment-title">{Resources.DeferredTo}</div>
                {!showEditDeferredTo &&
                    <div className="flex-row">
                        <Time
                            dateTime={props.deferredTo}
                            getNextInterval={() => -1}
                            getTimeString={() => {
                            let dayPart = props.deferredTo.toLocaleString(undefined, {weekday: "long", month: "short", day: "numeric" });
                            let timePart = new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "numeric" }).format(props.deferredTo);
                            return format(PlatformResources.DayAtTimeFormat, dayPart, timePart)}}
                            tooltipProps={{renderContent: () => DateUtil.tooltipString(props.deferredTo)}}
                        />
                        <Link className="approval-edit-defer-date" onClick={() => setShowEditDeferredTo(true)}>{Resources.Edit}</Link>
                    </div>
                }
            {showEditDeferredTo &&
                <DeferApprovalArea deferredTo={props.deferredTo} onCancel={() => {setShowEditDeferredTo(false)}} onDefer={onDefer} />
            }
            </div>
        </div>
    );
}

interface IExtendsGroupProps {
    checks: ObservableArray<CheckDetails>;
}

const ExtendsGroup: React.SFC<IExtendsGroupProps> = (props) => {
    const renderChecks = (rowIndex: number, item: CheckDetails, details: IListItemDetails<CheckDetails>) => (
        <CheckListItem check={item} resultMessage={item.resultMessage} checkName={Resources.RequiredTemplate} details={details} rowIndex={rowIndex} />
    );

    return (
        <div>
            <List itemProvider={props.checks} renderRow={renderChecks} singleClickActivation={false} width="100%" />
        </div>
    );
};

interface IExclusiveLockGroupProps {
    checks: ObservableArray<ExclusiveLockDetails>;
}

const ExclusiveLockGroup: React.SFC<IExclusiveLockGroupProps> = (props) => {
    const context = useComponentContext();

    const renderChecks = (rowIndex: number, item: ExclusiveLockDetails, details: IListItemDetails<ExclusiveLockDetails>) => {
        const status = Utilities.getExclusiveLockStatusIndicatorData(item.status);
        let label: React.ReactChild = status.label;
        if (item.lockInUse && item.buildName && item.status == CheckRunStatus.Running) {
            // Custom label for linking back to the build with the lock
            label = (
                <div>
                    <span>{Resources.ExclusiveLockHeldBy + " "}</span>
                    <Link className="pipelines-resource-link" href={getBuildUrl(context.pageContext, item.projectId, item.buildId)}>
                        {item.buildName}
                    </Link>
                </div>
            );
        }
        return (
            <CheckListItem
                check={item}
                resultMessage={item.resultMessage}
                checkName={Resources.ExclusiveLock}
                details={details}
                rowIndex={rowIndex}
                statusIndicator={status}
                statusLabel={() => Promise.resolve(label)}
            />
        );
    };

    return (
        <div>
            <List itemProvider={props.checks} renderRow={renderChecks} singleClickActivation={false} width="100%" />
        </div>
    );
};

interface ICheckListItemProps {
    check: any;
    arePendingChecksPresent?: boolean;
    showCheckIssues?: boolean;
    resultMessage?: string;
    checkName: string;
    details: IListItemDetails<ApprovalDetails | TaskCheckDetails | CheckDetails | ManualValidationDetails>;
    rowIndex: number;
    statusIndicator?: Utilities.IStatusIndicatorData;
    statusLabel?: () => Promise<React.ReactChild>;
    onRerunCheck?: (check: any) => void;
    onBypassCheck?: (check: any) => void;
}

const CheckListItem: React.SFC<ICheckListItemProps> = (props) => {
   
    const statusIndicatorData = props.statusIndicator || Utilities.getCheckRunStatusIndicatorData(props.check.status);
    const isManualValidation = equals(props.check.typeId, Utilities.ManualValidationTaskTypeId, true); 
    const [statusLabel, setStatusLabel] = React.useState<React.ReactChild>();

    if (props.statusLabel) {
        props.statusLabel().then((value) => {
            setStatusLabel(value);
        });
    } 

    if (isManualValidation) {
        const mvStatusIndicatorData = Utilities.getApprovalStatusIndicatorData(props.check.status, true);
        let mvStatusLabel = mvStatusIndicatorData.label;

        if (mvStatusLabel === Resources.Waiting && props.check.instructions) {
            mvStatusLabel = format(Resources.WaitingFormat, "\"" + props.check.instructions + "\"")
        }

        return (
            <div>
                <ListItem key={"manual-validation-item" + props.rowIndex} index={props.rowIndex} details={props.details}>
                    <div className="checks-row flex-row flex-grow flex-center h-scroll-hidden">
                        <Status {...mvStatusIndicatorData.statusProps} size={StatusSize.m} />
                        <div className="checks-row-details flex-column flex-grow h-scroll-hidden">
                            <div className="flex-row flex-center h-scroll-hidden">
                                <span className="checks-row-title body-m text-ellipsis">{props.checkName}</span>
                                <span className="pipelines-resource body-s text-ellipsis secondary-text">{props.check.jobName}</span>
                            </div>
                            <span className="body-s text-ellipsis secondary-text">{mvStatusLabel}</span>
                        </div>
                    </div>
                </ListItem>
            </div>
        );
    }

    const resourceName = props.check.resource ? props.check.resource.name : "";
    const resourceIconName = props.check.resource ? Utilities.getResourceTypeIcons(props.check.resource.type) : "Server"; //Default icon

    const resourceType = props.check.resource ? Utilities.getResourceTypeString(props.check.resource.type) : "";
    const context = useComponentContext();

    var menuItems: IMenuItem[] = getContextMenuItems(props);
    return (
        <ListItem key={"approval-item" + props.rowIndex} index={props.rowIndex} details={props.details}>
            <div className="checks-row flex-row flex-grow flex-center h-scroll-hidden">
                <Status {...statusIndicatorData.statusProps} size={StatusSize.m} />
                <div className="checks-row-details flex-column flex-grow h-scroll-hidden">
                    <div className="flex-row flex-center h-scroll-hidden">
                        <span className="checks-row-title body-m text-ellipsis">{props.checkName}</span>
                        { !!props.showCheckIssues && !!props.check.issue && (
                            <div className="checks-issue-icon">
                                <Icon
                                    className={css(props.check.issue.type === CheckIssueType.Warning && "warning",
                                                    props.check.issue.type === CheckIssueType.Error && "error")}
                                    iconName={props.check.issue.type === CheckIssueType.Error ? "ErrorBadge" : "Warning"}
                                    tooltipProps={{text: props.check.issue.message}}
                                />
                            </div>
                        )}
                        <Icon className="pipelines-resource-icon-1 secondary-text" iconName={resourceIconName} />
                        <span className="pipelines-resource body-s text-ellipsis secondary-text">{resourceType}</span>
                        <span className="pipelines-resource body-s text-ellipsis">
                            <Link className="pipelines-resource-link" href={getResourceUrl(context.pageContext, props.check.resource, true)}>
                                {resourceName}
                            </Link>
                        </span>
                    </div>
                    <span className="body-s text-ellipsis secondary-text">{statusLabel || statusIndicatorData.label}</span>
                </div>
                { menuItems?.length > 0 &&
                    <div className="check-actions">
                        <MoreButton
                            ariaLabel={Resources.MoreTooltip}
                            className="check-more-button"
                            excludeTabStop={true}
                            hideDropdownIcon={true}
                            contextualMenuProps={{
                                menuProps: { id: "header-submenu", items: menuItems }
                            }}
                            tooltipProps={{ text: Resources.MoreTooltip }}
                        />
                    </div>
                }
            </div>
        </ListItem>
    );
};

interface IStageGroupHeaderProps {
    myApprovalsCount: number;
    myManualValidationsCount: number;
    otherChecksCount: number;
    stageName: string;
    stageTimeoutString: string;
    stageTimeoutStringToolTip: string;
    reviewMode: ReviewMode;
    openReviewDialog: (status: ApprovalStatus) => void;
    onViewStageChecksClicked?: () => void,
}

const StageGroupHeader: React.SFC<IStageGroupHeaderProps> = (props) => {
    const shouldShowStage = props.reviewMode === ReviewMode.AllPending && props.stageName && props.stageName !== defaultStageName;
    return (
        <div className="flex-row body-m">
            <div className="flex-column flex-grow justify-end">
                {shouldShowStage && (
                    <>
                        <div className="flex-row checks-group-header-title">
                            <Tooltip overflowOnly={true} text={props.stageName}>
                                <span className="title-s text-ellipsis stage-header">{props.stageName}</span>
                            </Tooltip>
                        {!!props.onViewStageChecksClicked && props.myManualValidationsCount === 0 && (
                                <Link className="body-s checks-view-link" onClick={props.onViewStageChecksClicked}>{Resources.ViewAll}</Link>
                            )}
                        </div>
                        {props.reviewMode === ReviewMode.Stage && (
                            <Tooltip text={props.stageTimeoutStringToolTip}>
                                <span className="body-m text-ellipsis secondary-text stage-timeout-string">{props.stageTimeoutString}</span>
                            </Tooltip>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

interface IChecksGroup {
    arePendingChecksPresent: boolean;
    checks: ObservableArray<TaskCheckDetails>;
    showCheckIssues?: boolean;
    onSelectCheck: (taskCheck: TaskCheckDetails, arePendingChecksPresentInStage: boolean) => void;
    onRerunCheck: (check: any) => void;
    onBypassCheck: (check: any) => void;
}

const ChecksGroup: React.SFC<IChecksGroup> = (props) => {
    const onListItemActivate = (event: React.SyntheticEvent<HTMLElement>, listRow: IListRow<TaskCheckDetails>) => {
        if (!event.defaultPrevented) {
            props.onSelectCheck(listRow.data, props.arePendingChecksPresent);
        }
    };

    const renderChecks = (rowIndex: number, item: TaskCheckDetails, details: IListItemDetails<TaskCheckDetails>) => {
        const status = Utilities.getCheckRunStatusIndicatorData(item.status);

        if (item.status === CheckRunStatus.Bypassed) {
            let modifiedByIdentity = Utilities.getCheckRunUpdateModifiedByIdentity(item.status, item.resultUpdates);
            if (modifiedByIdentity) {
                status.detailedLabel = format(Resources.BypassedBy, modifiedByIdentity.displayName);
            }
        }

        return (
            <CheckListItem
                check={item}
                details={details}
                rowIndex={rowIndex}
                arePendingChecksPresent={props.arePendingChecksPresent}
                showCheckIssues={props.showCheckIssues}
                checkName={item.taskCheckConfigSettings.displayName}
                statusLabel={() => getTaskCheckStatusString(item, status, props.arePendingChecksPresent!)}
                onRerunCheck={props.onRerunCheck}
                onBypassCheck={props.onBypassCheck}
            />
        );
    };

    return (
        <div>
            <List itemProvider={props.checks} renderRow={renderChecks} singleClickActivation={true} width="100%" onActivate={onListItemActivate} />
        </div>
    );
};

function shouldIncludeApproval(approval: ApprovalDetails): boolean {
    if (Utilities.isCheckPermissionUpdate(approval.permissions)) {
        return true;
    }

    let myUninitiatedApprovalStep = approval.steps.filter(
        (a) => a.status === ApprovalStatus.Uninitiated && a.permissions === ApprovalPermissions.Update
    );
    let myPendingApprovalStep = approval.steps.filter((a) => a.status === ApprovalStatus.Pending && a.permissions === ApprovalPermissions.Update);
    return myUninitiatedApprovalStep.length > 0 && myPendingApprovalStep.length === 0;
}

function getOtherApprovals(approvals: ApprovalDetails[]): ApprovalDetails[] {
    let otherApprovals = approvals.filter((a) => a.permissions === ChecksPermission.View || shouldIncludeApproval(a));
    otherApprovals.sort(Utilities.approvalSort);
    return otherApprovals;
}

function getOtherManualValidations(manualValidations: ManualValidationDetails[]): ManualValidationDetails[] {
    let otherManualValidations = manualValidations.filter((a) => a.permissions === ChecksPermission.View);
    return otherManualValidations;
}

function filterByEvaluationOrder(checks: CheckDetails[], evaluationOrder: Number ): CheckDetails[] {
    if(evaluationOrder){
        return checks.filter((a) => a.evaluationOrder === evaluationOrder);
    }
    return evaluationOrder;
}

// Checks whether the current user is blocked in one or more approval.
function isCurrentUserBlockedInAnyApproval(approvals: ApprovalDetails[]): boolean {
    const context = useComponentContext();
    const service = context.pageContext.getService<IChecksPanelService>("IChecksPanelService");

    return service.isCurrentUserBlockedFromApproving(approvals);
}

function getContextMenuItems(props: ICheckListItemProps): IMenuItem[] {
    const menuItems: IMenuItem[] = [];

    if (props.arePendingChecksPresent && props.onRerunCheck && isRerunnable(props.check)){
        menuItems.push({
            id: "rerun-check",
            onActivate: () => (props.onRerunCheck && props.onRerunCheck(props.check)),
            text: "Rerun check"
        });
    }

    if (props.arePendingChecksPresent && props.onBypassCheck && isBypassable(props.check)){
        menuItems.push({
            id: "bypass-check",
            onActivate: () => (props.onBypassCheck && props.onBypassCheck(props.check)),
            text: Resources.BypassCheck
        });
    }
    return menuItems;
}


export function getCheckName(check: CheckDetails): string {
    if (equals(check.typeId, Utilities.ApprovalCheckTypeIdString, true)) {
        let approvalCheck = check as ApprovalDetails;
        if (!approvalCheck || !approvalCheck.definitionRef) {
            return Resources.Approval;
        }

        if(equals(MainApprovalDefTypeId, approvalCheck.definitionRef.id, true)){
            return Resources.Approval;
        }
        if(equals(PreApprovalDefTypeId, approvalCheck.definitionRef.id, true)){
            return Resources.PreApproval;
        }
        if(equals(PostApprovalDefTypeId, approvalCheck.definitionRef.id, true)){
            return Resources.PostApproval;
        }
        return Resources.Approval;
    } else if (equals(check.typeId, Utilities.ExtendsCheckTypeIdString, true)) {
        return Resources.RequiredTemplate;
    } else if (equals(check.typeId, Utilities.ExclusiveLockCheckTypeIdString, true)) {
        return Resources.ExclusiveLock;
    } else if (equals(check.typeId, Utilities.ManualValidationTaskTypeId, true)) {
        return Resources.ManualValidation;
    } else if (equals(check.typeId, Utilities.TaskCheckTypeIdString, true)) {
        let taskCheck = check as TaskCheckDetails;
        return taskCheck !== null ? taskCheck.taskCheckConfigSettings.displayName : "";
    }

    return "";
}

function isRerunnable(check: CheckDetails): boolean {
    const context = useComponentContext();
    if (!isFeatureFlagEnabled(context.pageContext, "Pipelines.Checks.EnableRerunChecks", false)) {
        return false;
    }

    if (check.permissions !== ChecksPermission.ResourceAdmin) {
        // the user needs to be a resource admin
        return false;
    }

    if (check === null || !equals(check.typeId, Utilities.TaskCheckTypeIdString, true)) {
        // must be a task check type
        return false;
    }

    let taskCheck = check as TaskCheckDetails;
    return !checkRerunNotSupportedList.includes(taskCheck.taskCheckConfigSettings.definitionRef.id) &&
        taskCheck.taskCheckConfigSettings.retryInterval == 0 &&
        taskCheck.status == CheckRunStatus.Approved;
}

function isBypassable(check: CheckDetails): boolean {
    const context = useComponentContext();
    let isBypassTaskChecksEnabled = isFeatureFlagEnabled(context.pageContext, "Pipelines.Checks.EnableBypassTaskChecks", false);
    let isBypassApprovalsEnabled = isFeatureFlagEnabled(context.pageContext, "Pipelines.Checks.EnableBypassApprovals", false);
    let isBypassFeatureEnabled = isBypassTaskChecksEnabled || isBypassApprovalsEnabled;
    const isChecksOrderingEnabled = isFeatureFlagEnabled(context.pageContext, "Pipelines.Checks.EnableOrderingChecks", false);

    if (!isBypassFeatureEnabled || check.permissions !== ChecksPermission.ResourceAdmin) {
        // the user needs to be a resource admin
        return false;
    }

    if (isChecksOrderingEnabled && check.status === CheckRunStatus.Queued) {
        // Future check bypass not yet supported
        return false;
    }

    if (isBypassTaskChecksEnabled && equals(Utilities.TaskCheckTypeIdString, check.typeId, true)) {
        let taskCheck = check as TaskCheckDetails;
        let isBypassOperationSupported = !checkBypassNotSupportedList.includes(taskCheck.taskCheckConfigSettings.definitionRef.id);

        let isBusinessHoursCheck = taskCheck.taskCheckConfigSettings.definitionRef.id === Utilities.BusinessHoursPolicyCheckTypeIdString;

        let isCheckWithNoRetriesApproved =
            !isBusinessHoursCheck && taskCheck.taskCheckConfigSettings.retryInterval === 0 && check.status === CheckRunStatus.Approved;

        return isBypassOperationSupported && !isCheckWithNoRetriesApproved && check.status !== CheckRunStatus.Bypassed;
    }

    if (isBypassApprovalsEnabled && equals(Utilities.ApprovalCheckTypeIdString, check.typeId, true)) {
        return (check.status & CheckRunStatus.Completed) === 0;
    }

    return false;
}

export function getStageTimeoutDetails(stageData: ChecksPanelData) {
    // The timeout for a stage is the minimum timeout value of all the checks present in that stage.
    let minimumTimeout = Number.MAX_SAFE_INTEGER;
    let checkWithMinimumTimeout: CheckDetails = {} as CheckDetails;
    let remainingTimeoutInMinutes = 0;
    let checksCount = 0;
    let stageTimeoutMessage = "";
    let stageTimeoutMessageToolTip = "";

    // If there are no checks that are pending, then that means that the stage has completed and we can skip the timeout message.
    if (!arePendingChecksPresent(stageData)) {
        return {};
    }

    let checksList: CheckDetails[] = [];
    if (stageData.approvals) {
        checksList = checksList.concat(stageData.approvals);
    }

    if (stageData.manualValidations) {
        checksList = checksList.concat(stageData.manualValidations);
    }

    if (stageData.checks) {
        checksList = checksList.concat(stageData.checks);
    }

    // Only pending approvals, manualValdations and checks (except task checks) are taken into account, as completed approvals don't timeout.
    checksList.length > 0 &&
        checksList.map((check) => {
            if (check.status === CheckRunStatus.Running) {
                checksCount++;
                remainingTimeoutInMinutes = Utilities.getRemainingMinutesForCheckTimeout(check.timeout, check.createdDate);
                if (remainingTimeoutInMinutes < minimumTimeout) {
                    minimumTimeout = remainingTimeoutInMinutes;
                    checkWithMinimumTimeout = check;
                }
            }
        });

    // In contrast to other checks,even completed task checks can timeout when the stage is waiting on checks.
    // Evaluate Artifact Policies need a special handling since they don't get timed out once they pass.
    stageData.taskChecks &&
        stageData.taskChecks.map((check) => {
            if (equals(Utilities.EvaluateArtifactPolicyCheckTypeIdString, check.taskCheckConfigSettings.definitionRef.id, true)) {
                if (check.status === CheckRunStatus.Running) {
                    checksCount++;
                    remainingTimeoutInMinutes = Utilities.getRemainingMinutesForCheckTimeout(check.timeout, check.createdDate);
                    if (remainingTimeoutInMinutes < minimumTimeout) {
                        minimumTimeout = remainingTimeoutInMinutes;
                        checkWithMinimumTimeout = check;
                    }
                }
            } else if (check.status === CheckRunStatus.Running ||
                check.status === CheckRunStatus.Rerunning ||
                (check.status === CheckRunStatus.Approved && check.taskCheckConfigSettings.retryInterval > 0)) {
                checksCount++;
                remainingTimeoutInMinutes = Utilities.getRemainingMinutesForCheckTimeout(check.timeout, check.createdDate);
                if (remainingTimeoutInMinutes < minimumTimeout) {
                    minimumTimeout = remainingTimeoutInMinutes;
                    checkWithMinimumTimeout = check;
                }
            }
        });

    if (minimumTimeout === Number.MAX_SAFE_INTEGER) {
        return {};
    }

    stageTimeoutMessage = Utilities.getCheckTimeoutOrReevaluationString(Resources.Timeout, minimumTimeout);
    stageTimeoutMessageToolTip =
        checksCount === 1
            ? format(Resources.StageTimeoutMessageToolTipWithSingleCheck, getCheckName(checkWithMinimumTimeout))
            : format(Resources.StageTimeoutMessageToolTipWithMultipleChecks, getCheckName(checkWithMinimumTimeout), checksCount - 1);

    return { stageTimeoutMessage, stageTimeoutMessageToolTip };
}

export async function getTaskCheckStatusString(check: CheckDetails, statusIndicator: Utilities.IStatusIndicatorData, arePendingChecksPresent: boolean): Promise<string> {
    const context = useComponentContext();
    const isChecksLogsDisabled = isFeatureFlagEnabled(context.pageContext, "Pipelines.Checks.DisableUpdatedChecksLogs", false)
    const isAsyncCheckLogsFlowEnabled = isFeatureFlagEnabled(context.pageContext, "Pipelines.Checks.EnableUpdatedChecksLogsAsync", false)
    const tfsPageService = context.pageContext.getService<ITfsPageService>("ITfsPageService");
    const taskCheckLogLineService = context.pageContext.getService<ITaskCheckLogLineService>("ITaskCheckLogLineService");
    
    const tfsPageData = tfsPageService.getData();
    const projetId = tfsPageData?.project?.id || ""

    // Only for task checks except artifact policies the format is {CheckStatus} . {Reevaluate in X minutes}
    let checkStatusString = statusIndicator.detailedLabel ?? statusIndicator.label;
    if (equals(check.typeId, Utilities.TaskCheckTypeIdString, true)) {
        let taskCheck = check as TaskCheckDetails;
        if (taskCheck !== null) {
            let attemptToLogIdMap = taskCheck.taskCheckData.attemptToTaskCheckTimelineRecordDataMap;
            let attemptsList = attemptToLogIdMap ? Object.keys(attemptToLogIdMap) : [];

            if (attemptsList.length > 0) {
                let latestAttempt = attemptsList.sort()[attemptsList.length - 1];

                if (!equals(taskCheck.taskCheckConfigSettings.definitionRef.id, Utilities.EvaluateArtifactPolicyCheckTypeIdString, true) &&
                    taskCheck.taskCheckConfigSettings &&
                    taskCheck.taskCheckConfigSettings.retryInterval > 0 &&
                    (check.status === CheckRunStatus.Approved || check.status === CheckRunStatus.Running)
                    ) {

                    const latestAttemptFinishTime = attemptToLogIdMap[parseInt(latestAttempt)].finishTime;

                    if (arePendingChecksPresent && latestAttemptFinishTime) {
                        const now = new Date();
                        const reevaluationTime = latestAttemptFinishTime.getTime() + taskCheck.taskCheckConfigSettings.retryInterval * 60000 - now.getTime();

                        if (reevaluationTime >= 0) {
                            return format(Resources.CheckReevaluateStatusString,
                                checkStatusString,
                                Utilities.getCheckTimeoutOrReevaluationString(Resources.RetryString, reevaluationTime)
                                )
                        }
                    }                
                }
                
                if(!isChecksLogsDisabled){
                    const latestAttemptLog = attemptToLogIdMap[parseInt(latestAttempt)];
                    if(isAsyncCheckLogsFlowEnabled) { 
                        //For async flow fetch the logs only if check is still running or have failed
                        if((!latestAttemptLog.currentLogLine || latestAttemptLog.currentLogLine === "") && check.status === CheckRunStatus.Running || check.status === CheckRunStatus.Rejected || check.status === CheckRunStatus.TimedOut) {       
                            const currentLogLine = await taskCheckLogLineService.getLastCheckLogLine(context.pageContext, latestAttemptLog.planId, latestAttemptLog.logId, projetId, latestAttemptLog.lineCount);
                            latestAttemptLog.currentLogLine = currentLogLine;
                            attemptToLogIdMap[parseInt(latestAttempt)] = latestAttemptLog; //Update the map with the latest log line for caching
                        }
                    } 

                    //Include current log line in status
                    let logLine = latestAttemptLog.currentLogLine;
                    if (logLine && logLine !== "") {
                        logLine = "\"" + logLine + "\"";
                        switch (statusIndicator.label) {
                            case Resources.Failed:
                                checkStatusString = format(Resources.FailedFormat, logLine);
                                break;
                            case Resources.Canceled:
                                checkStatusString = format(Resources.CanceledFormat, logLine);
                                break;
                            case Resources.Passed:
                                checkStatusString = format(Resources.PassedFormat, logLine);
                                break;
                            case Resources.Queued:
                                checkStatusString = format(Resources.QueuedFormat, logLine);
                                break;
                            case Resources.InProgress:
                                checkStatusString = format(Resources.InProgressFormat, logLine);
                                break;
                            case Resources.TimedOut:
                                checkStatusString = format(Resources.TimedOutFormatLog, logLine);
                                break;
                            case Resources.Bypassed:
                                checkStatusString = format(Resources.GenericCheckLogFormat, checkStatusString, logLine);
                                break;
                        }
                    }
                }
            } else {
                if (statusIndicator.label === Resources.Failed && taskCheck.resultMessage) {
                    checkStatusString = format(Resources.FailedFormat, taskCheck.resultMessage);
                }
            }
        }
    }

    return checkStatusString;
}

export function arePendingChecksPresent(stageData: ChecksPanelData): boolean {
    return (
        (stageData.approvals && stageData.approvals.some((approval) => approval.status === CheckRunStatus.Running)) ||
        (stageData.approvals && stageData.approvals.some((approval) => approval.status === CheckRunStatus.Deferred)) ||
        (stageData.manualValidations && stageData.manualValidations.some((manualValidation) => manualValidation.status === CheckRunStatus.Running)) ||
        (stageData.checks && stageData.checks.some((check) => check.status === CheckRunStatus.Running)) ||
        (stageData.taskChecks && stageData.taskChecks.some((taskCheck) => taskCheck.status === CheckRunStatus.Running))
    );
}

export function shouldBypassChecksPanel(checkStages: ChecksPanelData[], permissionStages: ChecksPanelData[], hasNonApprovalChecks: boolean): boolean {
    let bypassChecksPanel = false;
    if (checkStages.length === 1 && permissionStages.length === 0 && !hasNonApprovalChecks) {
        //Bypass if a stage has only one check on a whole.
        if (checkStages[0].approvals && checkStages[0].taskChecks) {
            bypassChecksPanel = false;
        } else if (checkStages[0].approvals && checkStages[0].approvals.length === 1) {
            //BUG 1982647, users were not able to view nor approve/reject manual validation checks when approvals were present
            if (checkStages[0].manualValidations && checkStages[0].manualValidations.length === 0) {
                bypassChecksPanel = true;
            }
        } else if (checkStages[0].taskChecks && checkStages[0].taskChecks.length === 1) {
            bypassChecksPanel = true;
        } else if (checkStages[0].manualValidations && checkStages[0].manualValidations.length === 1) {
            bypassChecksPanel = true;
        }
    }
    return bypassChecksPanel;
}

Components.add("ms.tfs-build.stage-checks-panel", StageChecksPanel);
